import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../All CSS/Settings.css";

const API = process.env.REACT_APP_API_BASE_URL;

function InsertCalculation() {
  const [loading, setLoading] = useState(false);
  const [formOptions, setFormOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState("");
  const [selectedDisplayValue, setSelectedDisplayValue] = useState("");
  const [gridOptions, setGridOptions] = useState([]);
  const [selectedGrids, setSelectedGrids] = useState([]);
  const [gridSearchTerm, setGridSearchTerm] = useState("");
  const [isGridOpen, setIsGridOpen] = useState(false);
  const [hasLoadedGrid, setHasLoadedGrid] = useState(false);
  const [showNewFormPopup, setShowNewFormPopup] = useState(false);
  const [newFormName, setNewFormName] = useState("");
  const [tableRows, setTableRows] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const selectRef = useRef(null);
  const gridSelectRef = useRef(null);

  useEffect(() => {
    const loadFormOptions = async () => {
      try {
        setLoading(true);
        const res = await axios.post(
          `${API}/api/get-form-name-list-calculation`,
        );

        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setFormOptions(rows);
      } catch (error) {
        console.error("Form list load failed", error);
        toast.error("Failed to load form list");
      } finally {
        setLoading(false);
      }
    };

    loadFormOptions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (
        gridSelectRef.current &&
        !gridSelectRef.current.contains(event.target)
      ) {
        setIsGridOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const loadGridOptions = async () => {
      if (!selectedForm) {
        setGridOptions([]);
        setSelectedGrids([]);
        return;
      }

      try {
        const res = await axios.post(
          `${API}/api/get-grd-name-list-calculation`,
          {
            FormName: selectedForm,
          },
        );

        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        setGridOptions(rows);
        setSelectedGrids([]);
        setGridSearchTerm("");
        setIsGridOpen(false);
      } catch (error) {
        console.error("Grid list load failed", error);
        setGridOptions([]);
        setSelectedGrids([]);
        toast.error("Failed to load grid list");
      }
    };

    loadGridOptions();
  }, [selectedForm]);

  const filteredOptions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      return formOptions;
    }

    return formOptions.filter((option) => {
      const formName = option?.FormName || "";
      const menuName = option?.MenuName || "";

      return (
        formName.toLowerCase().includes(query) ||
        menuName.toLowerCase().includes(query)
      );
    });
  }, [formOptions, searchTerm]);

  const filteredGridOptions = useMemo(() => {
    const query = gridSearchTerm.trim().toLowerCase();

    if (!query) return gridOptions;

    return gridOptions.filter((option) =>
      String(option?.GrdName || "")
        .toLowerCase()
        .includes(query),
    );
  }, [gridOptions, gridSearchTerm]);

  const handleGridSelect = (option) => {
    const gridName = option?.GrdName || "";

    if (!gridName) {
      return;
    }

    setSelectedGrids((prev) => {
      const exists = prev.includes(gridName);
      return exists
        ? prev.filter((name) => name !== gridName)
        : [...prev, gridName];
    });

    setGridSearchTerm("");
    setHasLoadedGrid(false);
  };

  const handleSelect = (option) => {
    const formName = option.FormName || "";
    const menuName = option.MenuName || "";

    setSelectedForm(formName);
    setSelectedDisplayValue(
      menuName && formName ? `${menuName} - ${formName}` : formName,
    );
    setSelectedGrids([]);
    setGridOptions([]);
    setGridSearchTerm("");
    setIsGridOpen(false);
    setHasLoadedGrid(false);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleDisplay = async () => {
    if (!selectedForm) {
      toast.error("Please select a Form Name first");
      return;
    }

    const selectedGridNames = Array.from(
      new Set(selectedGrids.filter((name) => String(name).trim() !== "")),
    );

    setHasLoadedGrid(false);

    try {
      setLoading(true);

      const pendingRequests =
        selectedGridNames.length > 0
          ? selectedGridNames.map((gridName) =>
              axios.post(`${API}/api/get-calculation-grid-details`, {
                FormName: selectedForm,
                GrdName: gridName,
              }),
            )
          : [
              axios.post(`${API}/api/get-calculation-grid-details`, {
                FormName: selectedForm,
                GrdName: "",
              }),
            ];

      const responses = await Promise.all(pendingRequests);
      const allDetails = responses.flatMap((response) =>
        Array.isArray(response?.data?.data) ? response.data.data : [],
      );

      allDetails.sort((a, b) => {
        const tabOrderA = Number(a.TabOrder ?? 0);
        const tabOrderB = Number(b.TabOrder ?? 0);

        if (tabOrderA !== tabOrderB) {
          return tabOrderA - tabOrderB;
        }

        const colIndexA = Number(a.ColIndex ?? 0);
        const colIndexB = Number(b.ColIndex ?? 0);

        return colIndexA - colIndexB;
      });

      const mappedRows = allDetails.map((row, index) => ({
        ...row,
        id:
          row.id ??
          `${row.FormName ?? "row"}-${row.GrdName ?? "grid"}-${row.TabOrder ?? 0}-${row.ColIndex ?? 0}-${index}`,
      }));

      if (mappedRows.length > 0) {
        setTableRows(mappedRows);
        setHasLoadedGrid(true);

        const label =
          selectedGridNames.length > 0
            ? `Displaying ${selectedForm} - ${selectedGridNames.join(", ")}`
            : `Displaying ${selectedForm}`;

        toast.success(label);
      } else {
        setTableRows([]);
        setHasLoadedGrid(false);

        const label =
          selectedGridNames.length > 0
            ? `No settings found for ${selectedForm} - ${selectedGridNames.join(", ")}`
            : `No settings found for ${selectedForm}`;

        toast.info(label);
      }
    } catch (error) {
      console.error("Display failed:", error);
      setTableRows([]);
      setHasLoadedGrid(false);
      toast.error(
        error?.response?.data?.message ||
          "Unable to load selected form settings",
      );
    } finally {
      setLoading(false);
    }
  };

  const normalizedTableRows = useMemo(
    () =>
      tableRows.map((row, index) => ({
        ...row,
        id:
          row.id ??
          `${row.FormName ?? "row"}-${row.ObjectCode ?? "obj"}-${row.ColIndex ?? index}-${index}`,
      })),
    [tableRows],
  );

  const tableColumns = useMemo(() => {
    if (!normalizedTableRows.length) {
      return [];
    }

    const fields = Object.keys(normalizedTableRows[0]).filter(
      (field) => field !== "id",
    );

    return fields.map((field) => ({
      field,
      headerName: field
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (char) => char.toUpperCase()),
      minWidth: field.toLowerCase().includes("query") ? 220 : 120,
      flex: field.toLowerCase().includes("query") ? 1.5 : 1,
      renderCell: (params) => {
        const value = params.value;
        const displayValue =
          value === null || value === undefined || value === "" ? "-" : value;

        return (
          <span className="insert-settings-grid-cell">{displayValue}</span>
        );
      },
    }));
  }, [normalizedTableRows]);

  const handleNewFormSubmit = async (event) => {
    event.preventDefault();

    const trimmedFormName = newFormName.trim();

    // ============================================
    // Validation
    // ============================================

    if (!selectedForm) {
      toast.error("Please select an existing Form Name first");
      return;
    }

    if (!trimmedFormName) {
      toast.error("Please enter Form Name");
      return;
    }

    if (trimmedFormName.toLowerCase() === selectedForm.toLowerCase()) {
      toast.error(
        "New Form Name must be different from the existing Form Name",
      );
      return;
    }

    try {
      setLoading(true);

      // ============================================
      // Call Backend
      // ============================================

      const res = await axios.post(`${API}/api/insert-new-grds-calculation`, {
        SourceFormName: selectedForm,
        FormName: trimmedFormName,
      });

      // ============================================
      // Backend Response
      // ============================================

      if (!res?.data?.success) {
        toast.error(
          res?.data?.message || "Failed to create new GRDS Calculation",
        );

        return;
      }

      const rowsInserted = Number(res?.data?.data?.rowsInserted || 0);

      // ============================================
      // Add New Form To Form Dropdown
      // ============================================

      setFormOptions((prev) => {
        const alreadyExists = prev.some(
          (option) =>
            String(option?.FormName || "").toLowerCase() ===
            trimmedFormName.toLowerCase(),
        );

        if (alreadyExists) {
          return prev;
        }

        return [
          ...prev,
          {
            FormName: trimmedFormName,
            MenuName: trimmedFormName,
          },
        ];
      });

      // ============================================
      // Select Newly Created Form
      // ============================================

      setSelectedForm(trimmedFormName);

      setSelectedDisplayValue(trimmedFormName);

      // ============================================
      // Reset Grid Selection
      // ============================================

      setSelectedGrids([]);
      setGridOptions([]);
      setGridSearchTerm("");
      setIsGridOpen(false);

      // ============================================
      // Close Popup
      // ============================================

      setShowNewFormPopup(false);
      setNewFormName("");

      // ============================================
      // Success Message
      // ============================================

      toast.success(
        `${rowsInserted} GRDS Calculation rows inserted successfully`,
      );

      // ============================================
      // Load New Form Details
      // ============================================

      const detailsRes = await axios.post(
        `${API}/api/get-calculation-grid-details`,
        {
          FormName: trimmedFormName,
          GrdName: "",
        },
      );

      const details = Array.isArray(detailsRes?.data?.data)
        ? detailsRes.data.data
        : [];

      const mappedDetails = details.map((row, index) => ({
        ...row,
        id:
          row?.id ??
          `${row?.FormName ?? "row"}-${
            row?.GrdName ?? "grid"
          }-${row?.FunctionLineID ?? index}-${index}`,
      }));

      setTableRows(mappedDetails);

      setHasLoadedGrid(details.length > 0);

      // ============================================
      // Reload Grid Options
      // ============================================

      const gridRes = await axios.post(
        `${API}/api/get-grd-name-list-calculation`,
        {
          FormName: trimmedFormName,
        },
      );

      const grids = Array.isArray(gridRes?.data?.data) ? gridRes.data.data : [];

      setGridOptions(grids);
    } catch (error) {
      console.error("Create new GRDS Calculation failed:", error);

      const message =
        error?.response?.data?.message ||
        "Unable to create new GRDS Calculation";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="insert-settings-page">
      <ToastContainer
        position="top-center"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
      />

      <div className="insert-settings-shell">
        {loading && (
          <div className="insert-settings-loading-overlay">
            <div className="insert-settings-loading-box">
              <ClipLoader size={32} color="#2563eb" />
              <p className="insert-settings-loading-text">
                Loading, please wait...
              </p>
            </div>
          </div>
        )}

        <div className="insert-settings-header">
          <div className="insert-settings-field-wrap" ref={selectRef}>
            <div
              className={`insert-settings-select-box ${isOpen ? "open" : ""}`}
              onClick={() => setIsOpen((prev) => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsOpen((prev) => !prev);
                }
              }}
            >
              <div className="insert-settings-selected-value">
                {selectedDisplayValue ? (
                  <span>{selectedDisplayValue}</span>
                ) : (
                  <span className="insert-settings-placeholder">
                    Select Form Name
                  </span>
                )}
              </div>

              <button
                type="button"
                className="insert-settings-select-toggle"
                aria-label="Toggle form list"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsOpen((prev) => !prev);
                }}
              >
                <KeyboardArrowDownIcon />
              </button>
            </div>

            {isOpen && (
              <div className="insert-settings-dropdown">
                <div className="insert-settings-search-box">
                  <SearchIcon className="insert-settings-search-icon" />
                  <input
                    type="text"
                    placeholder="Search form or menu..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="insert-settings-search-input"
                  />
                </div>

                <div className="insert-settings-options">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => {
                      const isSelected = option.FormName === selectedForm;

                      return (
                        <div
                          key={`${option.FormName}-${index}`}
                          className={`insert-settings-option ${isSelected ? "selected" : ""}`}
                          onClick={() => handleSelect(option)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleSelect(option);
                            }
                          }}
                        >
                          <div className="insert-settings-option-main">
                            <span className="insert-settings-option-name">
                              {option.FormName}
                            </span>
                          </div>

                          {isSelected && (
                            <CheckIcon className="insert-settings-option-check" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="insert-settings-no-results">
                      No matching form found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div
            className="insert-settings-field-wrap insert-settings-grid-field-wrap"
            ref={gridSelectRef}
          >
            <div
              className={`insert-settings-select-box ${isGridOpen ? "open" : ""} ${!selectedForm || gridOptions.length === 0 ? "disabled" : ""}`}
              onClick={() => {
                if (selectedForm && gridOptions.length > 0) {
                  setIsGridOpen((prev) => !prev);
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (
                  (event.key === "Enter" || event.key === " ") &&
                  selectedForm &&
                  gridOptions.length > 0
                ) {
                  event.preventDefault();
                  setIsGridOpen((prev) => !prev);
                }
              }}
            >
              <div className="insert-settings-selected-value">
                {selectedGrids.length > 0 ? (
                  <span>{selectedGrids.join(", ")}</span>
                ) : (
                  <span className="insert-settings-placeholder">
                    {selectedForm
                      ? "Select Grd Name"
                      : "Select Form Name first"}
                  </span>
                )}
              </div>

              <button
                type="button"
                className="insert-settings-select-toggle"
                aria-label="Toggle grid list"
                onClick={(event) => {
                  event.stopPropagation();
                  if (selectedForm && gridOptions.length > 0) {
                    setIsGridOpen((prev) => !prev);
                  }
                }}
              >
                <KeyboardArrowDownIcon />
              </button>
            </div>

            {isGridOpen && (
              <div className="insert-settings-dropdown">
                <div className="insert-settings-search-box">
                  <SearchIcon className="insert-settings-search-icon" />
                  <input
                    type="text"
                    placeholder="Search grid name..."
                    value={gridSearchTerm}
                    onChange={(event) => setGridSearchTerm(event.target.value)}
                    className="insert-settings-search-input"
                    autoFocus
                  />
                </div>

                <div className="insert-settings-options">
                  {filteredGridOptions.length > 0 ? (
                    filteredGridOptions.map((option, index) => {
                      const gridName = option?.GrdName || "";
                      const isSelected = selectedGrids.includes(gridName);

                      return (
                        <div
                          key={`${gridName}-${index}`}
                          className={`insert-settings-option ${isSelected ? "selected" : ""}`}
                          onClick={() => handleGridSelect(option)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleGridSelect(option);
                            }
                          }}
                        >
                          <div className="insert-settings-option-main">
                            <span className="insert-settings-option-name">
                              {gridName}
                            </span>
                          </div>

                          {isSelected && (
                            <CheckIcon className="insert-settings-option-check" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="insert-settings-no-results">
                      No matching grid found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            className="insert-settings-display-btn"
            onClick={handleDisplay}
            disabled={!selectedForm}
          >
            {hasLoadedGrid ? (
              <VisibilityIcon className="insert-settings-display-icon" />
            ) : (
              <VisibilityOffIcon className="insert-settings-display-icon" />
            )}
            Display
          </button>
        </div>

        {normalizedTableRows.length > 0 && (
          <div className="insert-settings-grid-card">
            <div className="insert-settings-grid-toolbar">
              <button
                type="button"
                className="insert-settings-new-form-btn"
                onClick={() => setShowNewFormPopup(true)}
              >
                <AddIcon className="insert-settings-new-form-icon" />
                New Form
              </button>
            </div>

            <div className="insert-settings-grid-wrapper">
              <DataGrid
                rows={normalizedTableRows}
                columns={tableColumns}
                getRowId={(row) => row.id}
                disableSelectionOnClick
                disableColumnFilter={false}
                density="compact"
                rowHeight={42}
                headerHeight={32}
                pagination
                pageSize={pageSize}
                rowsPerPageOptions={[100, 150, 200, 300]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                className="insert-settings-data-grid"
              />
            </div>
          </div>
        )}
      </div>

      {showNewFormPopup && (
        <div
          className="insert-settings-modal-overlay"
          onClick={() => setShowNewFormPopup(false)}
        >
          <div
            className="insert-settings-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="insert-settings-modal-header">
              <h3 className="insert-settings-modal-title">
                New GRDS Calculation
              </h3>

              <button
                type="button"
                className="insert-settings-modal-close"
                onClick={() => setShowNewFormPopup(false)}
                aria-label="Close new GRDS popup"
              >
                ×
              </button>
            </div>

            <form
              className="insert-settings-modal-form"
              onSubmit={handleNewFormSubmit}
            >
              {/* Form Name */}
              <div className="insert-settings-modal-field">
                <label htmlFor="insert-settings-form-name">Form Name</label>

                <input
                  id="insert-settings-form-name"
                  type="text"
                  value={newFormName}
                  onChange={(event) => setNewFormName(event.target.value)}
                  placeholder="Enter form name"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="insert-settings-modal-submit"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

export default InsertCalculation;
